import { FC, ReactNode, useMemo } from 'react';

import { getLegalName, isMarketingConsentCountry } from '~/app/lib/privacy';
import { selectUserCountry } from '~/app/lib/store/session/selectors';
import { toPublicEndpoint } from '~/app/lib/getPublicEndpoint';
import { useSelector } from '~/app/lib/store/redux';
import { useI18n } from '~/app/lib/i18n';
import Link from '~/app/components/Link2';
import Text from '~/app/components/Text';
import Box from '~/app/components/Box';

import {
  AUSTRIA,
  BELGIUM,
  CANADA,
  DENMARK,
  GERMANY,
  LUXEMBOURG,
  NETHERLANDS,
  NORWAY,
  SWITZERLAND,
  UNITED_STATES,
} from '~/app/lib/country/countryCodes';

const OrchardLegalFooter: FC = () => {
  const { t } = useI18n('item');
  const country = useSelector(selectUserCountry);

  let items: ReactNode[] = [
    <Link
      key="privacy"
      testId="privacyLink"
      withHoverStyle
      href={toPublicEndpoint(`/privacy/sme/${country.toLowerCase()}`)}
    >
      {t('orchardLegalFooter.privacy')}
    </Link>,
    <Link
      key="terms"
      testId="termsLink"
      targetBlank
      withHoverStyle
      href={toPublicEndpoint(`/terms/sme/${country.toLowerCase()}`)}
    >
      {t('orchardLegalFooter.terms')}
    </Link>,
  ];

  switch (country) {
    case DENMARK:
    case BELGIUM:
    case NETHERLANDS:
    case LUXEMBOURG:
    case NORWAY:
    case UNITED_STATES:
      items = [
        ...items,
        <Link
          key="how"
          testId="howWeUseYourDataLink"
          withHoverStyle
          targetBlank
          href="https://www.sonymusic.com/how-we-use-your-data/"
        >
          {t('orchardLegalFooter.howWeUseYourData')}
        </Link>,
        <Link
          key="doNotSell"
          testId="doNotSellDataLink"
          withHoverStyle
          targetBlank
          href="https://www.sonymusic.com/ccpa-contact-form/"
        >
          {t('orchardLegalFooter.doNotSellData')}
        </Link>,
      ];

      break;

    case GERMANY:
      items = [
        ...items,
        <Link
          key="legalNotice"
          withHoverStyle
          targetBlank
          testId="deLegalNoticeLink"
          href="https://www.sonymusic.de/Impressum/"
        >
          {t('orchardLegalFooter.legalNotice')}
        </Link>,
      ];

      break;

    case SWITZERLAND:
      items = [
        ...items,
        <Link
          key="legalNotice"
          withHoverStyle
          targetBlank
          testId="chLegalNoticeLink"
          href="https://www.sonymusic.ch/Impressum/"
        >
          {t('orchardLegalFooter.legalNotice')}
        </Link>,
      ];

      break;

    case AUSTRIA:
      items = [
        ...items,
        <Link
          key="legalNotice"
          withHoverStyle
          targetBlank
          testId="atLegalNoticeLink"
          href="https://www.sonymusic.at/Impressum/"
        >
          {t('orchardLegalFooter.legalNotice')}
        </Link>,
      ];

      break;

    case CANADA:
      items = [
        ...items,
        <Link
          key="aiUsage"
          withHoverStyle
          targetBlank
          testId="caAiUsageLink"
          href="https://www.sonymusic.ca/en/ai-usage-terms"
        >
          {t('orchardLegalFooter.aiUsage')}
        </Link>,
      ];

      break;
  }

  const legalName = useMemo(
    () =>
      isMarketingConsentCountry(country)
        ? getLegalName(country)
        : 'Sony Music Entertainment',
    [country]
  );

  return (
    <Box
      testId="orchardLegalFooter"
      padding="1rem 1.2rem 1.8rem"
      isCentered
      style={{
        color: '#999',
        fontSize: '1.2rem',
      }}
    >
      <Text centered>
        © {new Date().getFullYear()} {legalName}
      </Text>
      <Text margin="0.3rem 0 0" centered lineHeight={1.2}>
        {items.flatMap((item, index) => {
          if (index) {
            return [' | ', item];
          }

          return [item];
        })}
      </Text>
    </Box>
  );
};

export default OrchardLegalFooter;
